import React, { useState, useRef, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import { uploadEditedVideo } from '../graphqlService';
import { useAuth } from '../context/AuthContext';
import { debugLog } from '../utils/debugLog';
import { MAX_UPLOAD_FILE_BYTES, REVISION_FILE_LIMIT_COPY } from '../config/uploadConfig';
import { formatBytes } from '../utils/format';
import { brand, neutral, statusTone } from '../theme';

interface UploadRevisionModalProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
  /** The version this upload becomes (v3 → 3), for the title. */
  nextVersion?: number;
}

// Wording only a developer would read: network failures, status codes,
// masked server errors. The API's own refusals (a plan's file limit, a
// project that can't take a new cut) are written for people and pass through.
const TECHNICAL = /failed to fetch|networkerror|network request failed|load failed|status code|\(\d{3}\)|unexpected error|internal server|typeerror|graphql|json/i;

const uploadErrorText = (error: unknown): string => {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const firstLine = String(message || '').split('\n')[0].trim();
  if (!firstLine || TECHNICAL.test(firstLine)) {
    return 'The upload didn’t finish. Check your connection and try again.';
  }
  return firstLine;
};

/** "MP4", "MOV": the file's extension, upper-cased, for the file summary. */
const fileKind = (file: File) => {
  const extension = file.name.includes('.') ? file.name.split('.').pop() : '';
  return extension ? extension.toUpperCase() : 'Video';
};

const UploadRevisionModal: React.FC<UploadRevisionModalProps> = ({
  projectId,
  open,
  onClose,
  onUploaded,
  nextVersion,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  // Tracks pending navigation timers so they can be cancelled on unmount.
  // Without this a timer could call navigate() after the component was gone,
  // and StrictMode's double-mount scheduled two of them.
  const pendingTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  useEffect(() => () => {
    pendingTimersRef.current.forEach(clearTimeout);
    pendingTimersRef.current = [];
  }, []);
  const [notes, setNotes] = useState('');
  const [submitForReview, setSubmitForReview] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A large cut that stopped part way continues when it's uploaded again.
  const [resumeNotice, setResumeNotice] = useState<string | null>(null);

  const title = nextVersion ? `Upload v${nextVersion}` : 'Upload a revision';

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        setError('Choose a video file (MP4 or MOV).');
        return;
      }

      // The absolute ceiling (25 GB). The API applies the project's own limit,
      // which follows the creator's plan (never below 500 MB), before uploading.
      if (file.size > MAX_UPLOAD_FILE_BYTES) {
        setError(`This file is ${formatBytes(file.size)}. Revision files can be ${REVISION_FILE_LIMIT_COPY}.`);
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (uploading) return;
    const file = event.dataTransfer.files[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileSelect(fakeEvent);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!uploading) setDragging(true);
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) {
      setError('Choose a file to upload.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setResumeNotice(null);

    try {
      const token = await user.getIdToken(true);

      const result = await uploadEditedVideo(
        projectId,
        selectedFile,
        notes,
        submitForReview,
        token,
        setUploadProgress,
        {
          onResume: ({ uploadedBytes, totalBytes }) =>
            setResumeNotice(`Continuing your earlier upload of this file. ${formatBytes(uploadedBytes)} of ${formatBytes(totalBytes)} was already uploaded.`),
        }
      );

      setUploadProgress(100);
      setUploaded(true);

      debugLog('Upload successful:', result);

      // Reset form
      setSelectedFile(null);
      setNotes('');
      setSubmitForReview(true);

      // Close modal and notify parent
      pendingTimersRef.current.push(setTimeout(() => {
        setUploaded(false);
        onUploaded();
        onClose();
      }, 1000));

    } catch (uploadError: any) {
      console.error('Upload failed:', uploadError);
      setError(uploadErrorText(uploadError));
      setResumeNotice(null);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setNotes('');
      setSubmitForReview(true);
      setError(null);
      setResumeNotice(null);
      setUploadProgress(0);
      onClose();
    }
  };

  // The direct upload reports real progress (0–99 while the file is sent);
  // the rest is the server recording the cut.
  const progressKnown = uploadProgress > 0 && uploadProgress < 99;
  const progressLabel = uploaded || uploadProgress >= 100
    ? 'Upload complete'
    : uploadProgress >= 99
      ? 'Finishing up…'
      : progressKnown
        ? `Uploading… ${uploadProgress}%`
        : 'Uploading…';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={uploading}
      aria-labelledby="upload-revision-title"
    >
      <DialogTitle id="upload-revision-title" sx={{ fontWeight: 700, pr: 7 }}>
        {title}
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={handleClose}
        disabled={uploading}
        sx={{ position: 'absolute', right: 12, top: 12 }}
      >
        <CloseOutlinedIcon />
      </IconButton>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {error}
          </Alert>
        )}

        {/* File drop area */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragging(false)}
          onClick={() => !selectedFile && !uploading && fileInputRef.current?.click()}
          data-testid="revision-drop-zone"
          sx={{
            p: 3,
            mb: 2.5,
            textAlign: 'center',
            borderRadius: 3,
            border: '1.5px dashed',
            borderColor: dragging ? brand.copper : brand.borderStrong,
            bgcolor: dragging ? neutral[100] : neutral[50],
            cursor: selectedFile ? 'default' : 'pointer',
            transition: 'border-color 120ms ease, background-color 120ms ease',
            ...(selectedFile ? { borderStyle: 'solid', borderColor: brand.border, bgcolor: 'background.paper', textAlign: 'left' } : {}),
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={uploading}
            data-testid="revision-file-input"
          />

          {selectedFile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box
                aria-hidden="true"
                sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: neutral[100], border: `1px solid ${brand.border}`, color: brand.slate, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <VideoFileOutlinedIcon />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{selectedFile.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fileKind(selectedFile)} · {formatBytes(selectedFile.size)}
                </Typography>
              </Box>
              <Button size="small" onClick={() => setSelectedFile(null)} disabled={uploading}>
                Choose another file
              </Button>
            </Box>
          ) : uploaded ? (
            <Typography sx={{ fontWeight: 600, color: statusTone.done.fg }}>Upload complete</Typography>
          ) : (
            <Box>
              <CloudUploadOutlinedIcon aria-hidden="true" sx={{ fontSize: 36, color: brand.slate, mb: 1 }} />
              <Typography sx={{ fontWeight: 600 }} gutterBottom>
                Drag your cut here, or choose a file
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                MP4 or MOV, {REVISION_FILE_LIMIT_COPY}.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<CloudUploadOutlinedIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={uploading}
              >
                Choose file
              </Button>
            </Box>
          )}
        </Box>

        {/* Upload progress */}
        {uploading && resumeNotice && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {resumeNotice}
          </Alert>
        )}
        {uploading && (
          <Box sx={{ mb: 2.5 }} role="status" aria-live="polite">
            <Typography variant="body2" sx={{ mb: 0.75, fontVariantNumeric: 'tabular-nums' }}>
              {progressLabel}
            </Typography>
            <LinearProgress
              variant={progressKnown ? 'determinate' : 'indeterminate'}
              value={uploadProgress}
              aria-label="Upload progress"
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        {/* Notes */}
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Notes for the creator (optional)"
          placeholder="What changed in this version?"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={uploading}
          sx={{ mb: 1.5 }}
        />

        {/* Submit for review */}
        <FormControlLabel
          control={
            <Checkbox
              checked={submitForReview}
              onChange={(e) => setSubmitForReview(e.target.checked)}
              disabled={uploading}
            />
          }
          label="Send to the creator for review"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
          Leave this unticked to upload without asking for a review yet.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          startIcon={uploading ? undefined : <CloudUploadOutlinedIcon />}
        >
          {uploading ? 'Uploading…' : title}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadRevisionModal;
